import React, { useEffect, useMemo, useState } from 'react';
import { Switch, Route, Redirect, useRouteMatch } from 'react-router-dom';
import { Box, CssBaseline } from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import Submissions from './Submission/Submissions';
import SubmissionForm from './Submission/SubmissionForm';
import Home from './Home/Home';
import UserGuidePage from './Guide/UserGuidePage';
import AccountPage from './Account/AccountPage';

const Certification = () => {
  const { path } = useRouteMatch();
  const parentTheme = useTheme();
  const [isCertDark, setIsCertDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('certificationThemeMode') === 'dark';
  });

  const applyMode = (mode, { emit = true } = {}) => {
    const nextIsDark = mode === 'dark';
    if (nextIsDark === isCertDark) return;
    setIsCertDark(nextIsDark);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('certificationThemeMode', nextIsDark ? 'dark' : 'light');
      if (emit) {
        window.dispatchEvent(new Event('cert-theme-mode-changed'));
      }
    }
  };

  const toggleMode = () => {
    applyMode(isCertDark ? 'light' : 'dark');
  };

  useEffect(() => {
    const syncFromStorage = () => {
      if (typeof window === 'undefined') return;
      const stored = window.localStorage.getItem('certificationThemeMode');
      applyMode(stored === 'dark' ? 'dark' : 'light', { emit: false });
    };

    const handleToggleRequest = () => toggleMode();

    window.addEventListener('storage', syncFromStorage);
    window.addEventListener('cert-theme-toggle-request', handleToggleRequest);
    window.addEventListener('cert-theme-mode-changed', syncFromStorage);

    return () => {
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener('cert-theme-toggle-request', handleToggleRequest);
      window.removeEventListener('cert-theme-mode-changed', syncFromStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCertDark]);

  const certificationTheme = useMemo(() => {
    const shared = {
      ...parentTheme,
      palette: {
        ...parentTheme.palette,
        mode: isCertDark ? 'dark' : 'light',
        primary: {
          main: '#FFD200',
          light: '#ffe54c',
          dark: '#e6bd00',
          contrastText: '#000',
        },
        secondary: {
          main: '#2e2e2e',
          dark: '#1a1a1a',
          light: '#4a4a4a',
          contrastText: '#ffffff',
        },
        background: isCertDark
          ? {
              paper: '#141414',
              default: '#333333',
              elevated: '#222222',
              card: '#111111',
            }
          : parentTheme.palette.background,
        text: isCertDark
          ? {
              primary: 'rgba(245, 245, 245, 0.95)',
              secondary: 'rgba(255, 210, 0, 0.82)',
              disabled: 'rgba(255, 255, 255, 0.38)',
            }
          : parentTheme.palette.text,
        divider: isCertDark ? 'rgba(255,255,255,0.16)' : parentTheme.palette.divider,
      },
      typography: {
        ...parentTheme.typography,
        fontWeightBold: 700,
        fontWeightExtraBold: 800,
        fontSize: 19,
        body1: {
          ...(parentTheme.typography?.body1 || {}),
          fontSize: '1.16rem',
          lineHeight: 1.7,
        },
        body2: {
          ...(parentTheme.typography?.body2 || {}),
          fontSize: '1.10rem',
          lineHeight: 1.65,
        },
        subtitle1: {
          ...(parentTheme.typography?.subtitle1 || {}),
          fontSize: '1.22rem',
          lineHeight: 1.6,
        },
        subtitle2: {
          ...(parentTheme.typography?.subtitle2 || {}),
          fontSize: '1.12rem',
          lineHeight: 1.55,
        },
      },
      components: {
        ...parentTheme.components,
        MuiTypography: {
          styleOverrides: {
            ...(parentTheme.components?.MuiTypography?.styleOverrides || {}),
            body1: {
              ...(parentTheme.components?.MuiTypography?.styleOverrides?.body1 || {}),
              fontSize: '1.16rem',
            },
            body2: {
              ...(parentTheme.components?.MuiTypography?.styleOverrides?.body2 || {}),
              fontSize: '1.10rem',
            },
            subtitle1: {
              ...(parentTheme.components?.MuiTypography?.styleOverrides?.subtitle1 || {}),
              fontSize: '1.22rem',
            },
            subtitle2: {
              ...(parentTheme.components?.MuiTypography?.styleOverrides?.subtitle2 || {}),
              fontSize: '1.12rem',
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            ...(parentTheme.components?.MuiInputBase?.styleOverrides || {}),
            root: {
              ...(parentTheme.components?.MuiInputBase?.styleOverrides?.root || {}),
              fontSize: '1.10rem',
            },
            input: {
              ...(parentTheme.components?.MuiInputBase?.styleOverrides?.input || {}),
              fontSize: '1.10rem',
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            ...(parentTheme.components?.MuiInputLabel?.styleOverrides || {}),
            root: {
              ...(parentTheme.components?.MuiInputLabel?.styleOverrides?.root || {}),
              fontSize: '1.02rem',
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            icon: {
              color: isCertDark ? 'rgba(245, 245, 245, 0.95)' : undefined,
            },
            iconOpen: {
              color: isCertDark ? 'rgba(245, 245, 245, 0.95)' : undefined,
            },
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: isCertDark ? '#111111' : parentTheme.palette.background?.default,
              color: isCertDark ? 'rgba(245,245,245,0.95)' : parentTheme.palette.text?.primary,
            },
            '.MuiButton-root, .MuiButton-contained, .MuiFab-root': {
              boxShadow: 'none !important',
            },
            '.MuiButton-root:hover, .MuiButton-contained:hover, .MuiFab-root:hover': {
              boxShadow: 'none !important',
            },
            footer: isCertDark
              ? {
                  backgroundColor: '#161616',
                  color: 'rgba(245,245,245,0.9)',
                }
              : {},
            '#app-footer, .layout-footer, .cmt-footer, .Cmt-footer, .Cmt-footer-wrapper, [data-footer="app-footer"]': isCertDark
              ? {
                  backgroundColor: '#161616',
                  color: 'rgba(245,245,245,0.9)',
                }
              : {},
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              boxShadow: isCertDark
                ? '0px 8px 24px rgba(0, 0, 0, 0.4)'
                : parentTheme.shadows?.[2] || '0px 1px 3px rgba(0,0,0,0.2)',
              backgroundColor: isCertDark ? '#1c1c1c' : undefined,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            rounded: {
              borderRadius: 12,
            },
          },
        },
        ...(isCertDark
          ? {
              MuiPopover: {
                styleOverrides: {
                  paper: {
                    backgroundColor: '#141414',
                    color: 'rgba(245, 245, 245, 0.95)',
                    border: '1px solid rgba(255,255,255,0.16)',
                  },
                },
              },
              MuiDialog: {
                styleOverrides: {
                  paper: {
                    backgroundColor: '#141414',
                    color: 'rgba(245, 245, 245, 0.95)',
                    border: '1px solid rgba(255,255,255,0.16)',
                  },
                },
              },
              MuiDialogTitle: {
                styleOverrides: {
                  root: {
                    color: 'rgba(245, 245, 245, 0.95)',
                  },
                },
              },
              MuiDialogContent: {
                styleOverrides: {
                  root: {
                    color: 'rgba(245, 245, 245, 0.95)',
                  },
                },
              },
              MuiDialogActions: {
                styleOverrides: {
                  root: {
                    borderTop: '1px solid rgba(255,255,255,0.16)',
                  },
                },
              },
              MuiMenu: {
                styleOverrides: {
                  paper: {
                    backgroundColor: '#141414',
                    color: 'rgba(245, 245, 245, 0.95)',
                    border: '1px solid rgba(255,255,255,0.16)',
                  },
                },
              },
              MuiMenuItem: {
                styleOverrides: {
                  root: {
                    color: 'rgba(245, 245, 245, 0.95)',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255, 210, 0, 0.18)',
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'rgba(255, 210, 0, 0.18)',
                    },
                  },
                },
              },
            }
          : {}),
        MuiButton: {
          defaultProps: {
            disableElevation: true,
          },
          styleOverrides: {
            root: {
              fontWeight: 700,
              letterSpacing: '-0.02em',
              boxShadow: 'none',
            },
            contained: {
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
              },
              '&:active': {
                boxShadow: 'none',
              },
              '&.Mui-focusVisible': {
                boxShadow: 'none',
              },
            },
          },
        },
        MuiFab: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
              },
              '&:active': {
                boxShadow: 'none',
              },
              '&.Mui-focusVisible': {
                boxShadow: 'none',
              },
            },
          },
        },
      },
      overrides: {
        MuiTypography: {
          h1: {
            fontSize: 22,
            fontWeight: 'bold',
            textTransform: 'uppercase',
            fontStyle: 'italic',
            fontFamily: '"Trade Gothic Next LT Pro","Trade Gothic LT Pro","Trade Gothic LT Std","T Star Pro",sans-serif',
            '@media (min-width:1280px)': {
              fontSize: 36,
            },
          },
          h2: {
            fontSize: 20,
            fontWeight: 'bold',
            textTransform: 'uppercase',
            fontStyle: 'normal',
            fontFamily: '"Trade Gothic LT bdC","Trade Gothic LT Std","T Star Pro",sans-serif',
            '@media (min-width:1280px)': {
              fontSize: 24,
            },
          },
          h3: {
            fontSize: 18,
            fontWeight: 400,
            textTransform: 'uppercase',
            fontStyle: 'normal',
            fontFamily: '"Trade Gothic LT bdC","Trade Gothic LT Std","T Star Pro",sans-serif',
            '@media (min-width:1280px)': {
              fontSize: 20,
            },
          },
          h4: {
            fontSize: 16,
            textTransform: 'uppercase',
            fontWeight: 400,
            fontStyle: 'normal',
            fontFamily: '"Trade Gothic LT bdC","Trade Gothic LT Std","T Star Pro",sans-serif',
          },
          h5: {
            fontSize: 14,
            textTransform: 'uppercase',
            fontWeight: 400,
            fontFamily: '"Trade Gothic LT bdC","Trade Gothic LT Std","T Star Pro",sans-serif',
          },
          h6: {
            fontSize: 14,
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontFamily: '"Trade Gothic LT bdC","Trade Gothic LT Std","T Star Pro",sans-serif',
          },
          subtitle1: {
            fontSize: 16,
            fontWeight: 400,
            letterSpacing: 0.15,
          },
          subtitle2: {
            fontSize: 14,
            fontWeight: 'bold',
            letterSpacing: 0.1,
          },
          body1: {
            fontSize: 16,
            fontWeight: 400,
            letterSpacing: 0.5,
          },
          body2: {
            fontSize: 16,
            fontWeight: 400,
            letterSpacing: 0.25,
          },
        },
      },
    };

    return createTheme(shared);
  }, [isCertDark, parentTheme]);

  return (
    <ThemeProvider theme={certificationTheme}>
      <CssBaseline />
      <Box
        sx={{
          p: 0,
          position: 'relative',
          minHeight: 'calc(100vh - 327px)',
          bgcolor: certificationTheme.palette.background.default,
          color: certificationTheme.palette.text.primary,
        }}>
        <Switch>
          <Route path={`${path}/submissions/:submissionId/step/:stepNumber/:criteriaTypeId`} component={SubmissionForm} />
          <Route path={`${path}/submissions/:submissionId/step/:stepNumber`} component={SubmissionForm} />
          <Route path={`${path}/submissions`} component={Submissions} />
          <Route path={`${path}/user-manual`} component={UserGuidePage} />
          <Route path={`${path}/account`} component={AccountPage} />
          <Route exact path={path} component={Home} />
          <Redirect to={path} />
        </Switch>
      </Box>
    </ThemeProvider>
  );
};

export default Certification;
